import { HardDrive } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { StorageRoot } from '../lib/api'

interface Props {
  roots: StorageRoot[]
}

interface SettingsProps {
  root?: StorageRoot
  loading: boolean
}

export function StorageCapacity({ roots }: Props) {
  const { t } = useTranslation()
  if (roots.length === 0) return null

  const multiple = roots.length > 1
  const accessibleSummary = roots.map((root) => {
    const capacity = capacityDetails(root)
    return `${multiple ? `${root.path}: ` : ''}${capacity
      ? t('storage.freeOfTotal', { free: formatStorageGigabytes(capacity.usable), total: formatStorageGigabytes(capacity.total) })
      : t('storage.unavailable')}`
  }).join('. ')

  return <section className="sidebar-storage" title={accessibleSummary}
    aria-label={`${t('storage.title')}. ${accessibleSummary}`}>
    <HardDrive className="sidebar-storage-compact-icon" aria-hidden="true" />
    <div className="sidebar-storage-content">
      <header><HardDrive aria-hidden="true" /><strong>{t('storage.title')}</strong></header>
      <div className="sidebar-storage-list">
        {roots.map((root) => <StorageRootCapacity key={root.id} root={root} showPath={multiple} />)}
      </div>
    </div>
  </section>
}

function StorageRootCapacity({ root, showPath }: { root: StorageRoot; showPath: boolean }) {
  const { t } = useTranslation()
  const capacity = capacityDetails(root)
  if (!capacity) {
    return <div className="sidebar-storage-root unavailable" title={root.path}>
      {showPath && <code>{root.path}</code>}
      <span>{t('storage.unavailable')}</span>
    </div>
  }

  const freePercent = capacity.usable / capacity.total * 100
  const usedPercent = Math.max(0, Math.min(100, 100 - freePercent))
  const level = freePercent <= 10 ? 'critical' : freePercent <= 20 ? 'low' : ''
  const summary = t('storage.freeOfTotal', {
    free: formatStorageGigabytes(capacity.usable),
    total: formatStorageGigabytes(capacity.total),
  })

  return <div className={`sidebar-storage-root ${level}`} title={root.path}>
    {showPath && <code>{root.path}</code>}
    <span>{summary}</span>
    <div className="sidebar-storage-meter" role="progressbar"
      aria-label={t('storage.usedPercent', { percent: Math.round(usedPercent) })}
      aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(usedPercent)}>
      <i style={{ width: `${usedPercent}%` }} />
    </div>
  </div>
}

export function SettingsStorageCapacity({ root, loading }: SettingsProps) {
  const { t } = useTranslation()
  const capacity = root ? capacityDetails(root) : null
  if (!capacity) {
    return <div className={`settings-storage-capacity ${loading ? 'pending' : 'unavailable'}`}>
      <span>{t('storage.available')}</span>
      <strong>{loading ? t('common.loading') : t('storage.unavailable')}</strong>
    </div>
  }

  const usedPercent = Math.max(0, Math.min(100, 100 - capacity.usable / capacity.total * 100))
  const freePercent = 100 - usedPercent
  const level = freePercent <= 10 ? 'critical' : freePercent <= 20 ? 'low' : ''
  return <div className={`settings-storage-capacity ${level}`}>
    <div className="settings-storage-capacity-header">
      <span>{t('storage.available')}</span>
      <strong>{t('storage.free', { free: formatStorageGigabytes(capacity.usable) })}</strong>
    </div>
    <div className="settings-storage-meter" role="progressbar"
      aria-label={t('storage.usedPercent', { percent: Math.round(usedPercent) })}
      aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(usedPercent)}>
      <i style={{ width: `${usedPercent}%` }} />
    </div>
    <div className="settings-storage-capacity-meta">
      <span>{t('storage.usedOfTotal', {
        used: formatStorageGigabytes(capacity.total - capacity.usable),
        total: formatStorageGigabytes(capacity.total),
      })}</span>
      <strong>{t('storage.usedPercent', { percent: Math.round(usedPercent) })}</strong>
    </div>
  </div>
}

function capacityDetails(root: StorageRoot) {
  if (root.totalBytes === null || root.usableBytes === null || root.totalBytes <= 0) return null
  return {
    total: root.totalBytes,
    usable: Math.max(0, Math.min(root.usableBytes, root.totalBytes)),
  }
}

export function formatStorageGigabytes(bytes: number) {
  if (bytes <= 0) return '0 GiB'
  const value = bytes / 1024 ** 3
  return `${new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: value < 10 ? 1 : 0,
  }).format(value)} GiB`
}
